<script async setup>
import NumberAnimation from 'vue-number-animation'
import PhCircleWavyFill from '~icons/ph/circle-wavy-fill'

const firstPlaceWinners = ref([])
const secondPlaceWinners = ref([])
const thirdPlaceWinners = ref([])
const runnersUp = ref([])
const route = useRoute()

const firstPlaceScore = ref(0)
const secondPlaceScore = ref(0)
const thirdPlaceScore = ref(0)

function theFormat(number) {
  return number.toFixed(1)
}

const { data } = await useFetch(`/api/champs?year=${route.params.year ? route.params.year : '2023'}`, { key: route.params.year ? `champs_${route.params.year}` : 'champs_2023' })
firstPlaceWinners.value = data.value.firstPlaceWinners
secondPlaceWinners.value = data.value.secondPlaceWinners
thirdPlaceWinners.value = data.value.thirdPlaceWinners
runnersUp.value = data.value.runnersUp

firstPlaceScore.value = firstPlaceWinners.value[0]?.score
secondPlaceScore.value = secondPlaceWinners.value[0]?.score
thirdPlaceScore.value = thirdPlaceWinners.value[0]?.score
</script>

<template>
  <div class="pt-0 sm:pt-6 overflow-hidden">
    <div>
      <TheTopThreeDropdown />
    </div>

    <div class="flex justify-center dark:text-gray-50">
      <div class="inline-block relative justify-end">
        <div class="absolute w-full">
          <div class="flex justify-center items-center pt-4">
            <NuxtImg
              preload
              alt="left star"
              class="left-star max-w-[37px] max-h-[36px]"
              height="68"
              width="67"
              sizes="400px"
              src="/images/left-star.webp"
            />
            <NuxtImg
              preload
              alt="middle star"
              class="middle-star max-w-[60px] max-h-[58px]"
              height="68"
              width="67"
              sizes="400px"
              src="/images/middle-star.webp"
            />
            <NuxtImg
              preload
              alt="right star"
              class="right-star max-w-[37px] max-h-[36px]"
              height="67"
              width="68"
              sizes="400px"
              src="/images/right-star.webp"
            />
          </div>
        </div>

        <div class="flex justify-center select-none min-h-[221px]">
          <div class="">
            <NuxtImg
              preload
              height="898"
              width="650"
              alt="gold cup"
              sizes="221px"
              class="min-w-40 max-w-38 max-h-[221px]"
              src="/images/gold.webp"
            />
          </div>
        </div>
        <div class="absolute text-center top-40 sm:left-[calc(37%)] left-[calc(39%)] sg-medium">
          <div>
            <PhCircleWavyFill
              class="absolute text-green-500 text-4xl animate-spin"
              style="animation-duration:7s !important;"
            />
            <div class="relative text-white text-center w-6 text-2xl top-1.5 left-2.5">
              <client-only>
                <NumberAnimation
                  :from="0"
                  :to="firstPlaceWinners[0]?.score"
                  :format="num => num.toFixed(0)"
                  :duration="1"
                  autoplay
                  easing="linear"
                />
                <template #fallback>
                  0
                </template>
              </client-only>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-start items-center">
        <div class="">
          <div class="flex">
            <div
              v-show="firstPlaceWinners && firstPlaceWinners.length === 1"
              class="sg-semibold px-2 tracking-widest text-xl text-[#734E01] bg-[#fff758] text-center rounded rounded-md uppercase"
            >
              #1 champ
            </div>
            <div
              v-show="firstPlaceWinners && firstPlaceWinners.length >= 2"
              class="sg-semibold tracking-widest w-28 px-1 text-xl text-[#734E01] bg-[#fff758] text-center rounded rounded-md uppercase"
            >
              joint #1
            </div>
          </div>
          <div v-if="firstPlaceWinners && firstPlaceWinners.length === 1" class="pt-10">
            <client-only>
              <PopOverWrapper placement="right" hover :contestant="firstPlaceWinners[0]?.name_id">
                <NuxtLink
                  :to="`/champ/${firstPlaceWinners[0]?.name_id}`"
                  class="sg-bold text-4xl sm:text-5xl top-1-link"
                >
                  {{ firstPlaceWinners[0]?.name }}
                  <template #content />
                </NuxtLink>
              </PopOverWrapper>
              <template #fallback>
                <div class="sg-bold text-4xl sm:text-5xl top-1-link">
                  {{ firstPlaceWinners[0]?.name }}
                </div>
              </template>
            </client-only>
          </div>
          <div v-else>
            <div v-for="winner in firstPlaceWinners" :key="winner.name" class="py-3">
              <client-only>
                <PopOverWrapper placement="right" hover :contestant="winner?.name_id">
                  <NuxtLink :to="`/champ/${winner?.name_id}`" class="sg-semibold text-4xl top-1-link">
                    {{ winner?.name }}
                    <template #content />
                  </NuxtLink>
                </PopOverWrapper>
                <template #fallback>
                  <div class="sg-semibold text-4xl top-1-link">
                    {{ winner?.name }}
                  </div>
                </template>
              </client-only>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="sm:flex justify-around items-center md:-mt-0">
      <div class="flex">
        <div class="inline-block relative">
          <NuxtImg
            preload
            alt="silver cup"
            class="left-star max-w-[125px] max-h-[165px]"
            height="600"
            width="434"
            sizes="165px"
            src="/images/silver.webp"
          />
          <div class="absolute top-25 text-center bottom-10 left-[calc(30%)]">
            <div>
              <PhCircleWavyFill
                class="absolute text-green-500 text-4xl animate-spin"
                style="animation-duration:7s !important;"
              />
              <div class="relative text-white text-center w-6 text-2xl top-1.5 left-2.5">
                <client-only>
                  <NumberAnimation
                    :from="0"
                    :to="secondPlaceWinners[0]?.score"
                    :format="num => num.toFixed(0)"
                    :duration="1"
                    autoplay
                    easing="linear"
                  />
                  <template #fallback>
                    0
                  </template>
                </client-only>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center ml-0 sm:ml-6 ">
          <div class="">
            <div
              v-show="secondPlaceWinners && secondPlaceWinners.length === 1"
              class="sg-semibold tracking-widest w-9 text-xl text-[rgb(72, 68, 58)] bg-[#e1e1e2] text-center rounded rounded-md"
            >
              #2
            </div>
            <div
              v-show="secondPlaceWinners && secondPlaceWinners.length >= 2"
              class="sg-semibold tracking-widest w-28 px-1 text-xl text-[rgb(72, 68, 58)] bg-[#e1e1e2] text-center rounded rounded-md"
            >
              JOINT #2
            </div>
            <div v-if="secondPlaceWinners && secondPlaceWinners.length === 1">
              <client-only>
                <PopOverWrapper placement="right" hover :contestant="secondPlaceWinners[0]?.name_id">
                  <NuxtLink
                    :to="`/champ/${secondPlaceWinners[0]?.name_id}`"
                    class="sg-semibold text-3xl top-3-link dark:text-gray-50"
                  >
                    {{ secondPlaceWinners[0]?.name }}
                    <template #content />
                  </NuxtLink>
                </PopOverWrapper>
                <template #fallback>
                  <div class="sg-semibold text-3xl top-3-link dark:text-gray-50">
                    {{ secondPlaceWinners[0]?.name }}
                  </div>
                </template>
              </client-only>
            </div>
            <div v-else class="">
              <div v-for="winner in secondPlaceWinners" :key="winner.name" class="flex py-2">
                <client-only>
                  <PopOverWrapper placement="right" hover :contestant="winner?.name_id">
                    <NuxtLink
                      :to="`/champ/${winner?.name_id}`"
                      class="sg-semibold text-3xl py-2 top-3-link dark:text-gray-50"
                    >
                      {{ winner?.name }}
                      <template #content />
                    </NuxtLink>
                  </PopOverWrapper>
                  <template #fallback>
                    <div class="sg-semibold text-3xl top-3-link dark:text-gray-50">
                      {{ winner?.name }}
                    </div>
                  </template>
                </client-only>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="thirdPlaceWinners[0]?.score > 0" class="flex items-center">
        <div class="inline-block relative flex justify-center ">
          <div class="flex min-w-40 justify-center pt-10">
            <NuxtImg
              preload
              alt="silver cup"
              class="left-star max-h-[100px] max-w-[56px]"
              height="178"
              width="100"
              sizes="400px"
              src="/images/bronze.webp"
            />
          </div>
          <div class="absolute top-20 right-20 text-center">
            <div>
              <PhCircleWavyFill
                class="absolute text-green-500 text-4xl animate-spin"
                style="animation-duration:7s !important;"
              />
              <div class="relative text-white text-center w-6 text-2xl top-1.5 left-2.5">
                <client-only>
                  <NumberAnimation
                    :from="0"
                    tag="div"
                    :to="thirdPlaceWinners[0]?.score"
                    :format="num => num.toFixed(0)"
                    :duration="1"
                    autoplay
                    easing="linear"
                  />
                  <template #fallback>
                    0
                  </template>
                </client-only>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center">
          <div class="">
            <div
              v-show="thirdPlaceWinners && thirdPlaceWinners.length === 1"
              class="sg-semibold tracking-widest w-9 text-xl text-[#4f240f] bg-[#d99e62] text-center rounded rounded-md "
            >
              #3
            </div>

            <div
              v-show="thirdPlaceWinners && thirdPlaceWinners.length >= 2"
              class="sg-semibold tracking-widest w-28 px-1 text-xl text-[#4f240f] bg-[#d99e62] text-center rounded rounded-md"
            >
              JOINT #3
            </div>
            <div v-if="thirdPlaceWinners && thirdPlaceWinners.length === 1">
              <client-only>
                <PopOverWrapper placement="right" hover :contestant="thirdPlaceWinners[0]?.name_id">
                  <NuxtLink
                    :to="`/champ/${thirdPlaceWinners[0]?.name_id}`"
                    class="sg-semibold text-3xl top-3-link dark:text-gray-50"
                  >
                    {{ thirdPlaceWinners[0]?.name }}
                    <template #content />
                  </NuxtLink>
                </PopOverWrapper>
                <template #fallback>
                  <div class="sg-semibold text-3xl top-3-link dark:text-gray-50">
                    {{ thirdPlaceWinners[0]?.name }}
                  </div>
                </template>
              </client-only>
            </div>
            <div v-for="winner in thirdPlaceWinners" v-else :key="winner.name" class="flex py-2">
              <client-only>
                <PopOverWrapper placement="right" hover :contestant="winner?.name_id">
                  <NuxtLink
                    :to="`/champ/${winner?.name_id}`"
                    class="sg-semibold text-2xl py-2 top-3-link dark:text-gray-50"
                  >
                    {{ winner?.name }}
                    <template #content />
                  </NuxtLink>
                </PopOverWrapper>
                <template #fallback>
                  <div class="sg-semibold text-2xl py-2 top-3-link dark:text-gray-50">
                    {{ winner?.name }}
                  </div>
                </template>
              </client-only>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="text-xl pt-8 mt-6 pb-5 text-center sm:text-2xl text-black sg-medium bg-green-100 border-t-4 border-green-400 border-dashed dark:text-gray-50 dark:bg-gray-800"
    >
      ... and the runners-up
    </div>
    <div class="flex flex-col pt-4 bg-green-100 -mb-2 sm:mb-0 sm:pb-2 dark:bg-gray-800">
      <div class="-my-2 overflow-x-auto lg:-mx-8">
        <div class="py-2 align-middle inline-block min-w-full max-w-full sm:px-6 lg:px-8">
          <div class="overflow-hidden">
            <table class="min-w-full">
              <thead class="sg-medium text-md h-10">
                <tr class="">
                  <th class="text-center text-white uppercase tracking-wider" scope="col">
                    <span class="bg-gray-600 px-3">Rank</span>
                  </th>
                  <th class="text-left text-white uppercase tracking-wider" scope="col">
                    <span class="bg-gray-600 px-3">Name</span>
                  </th>
                  <th
                    class="text-white uppercase tracking-wider text-center hidden sm:flex sm:justify-center sm:items-center sm:h-10"
                    scope="col"
                  >
                    <span class="bg-gray-600 px-3"> # Games Played in</span>
                  </th>
                  <th class="text-center text-white uppercase tracking-wider" scope="col">
                    <span class="bg-gray-600 px-3">Lost</span>
                  </th>
                  <th class="text-white uppercase tracking-wider text-center" scope="col">
                    <span class="bg-gray-600 px-3">Won</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 text-gray-600 dark:text-gray-50 ">
                <tr
                  v-for="contestant in runnersUp"
                  :key="contestant.name"
                  class="&hover:bg-green-500 &hover:text-white dark:hover:bg-green-600"
                >
                  <td class="py-1 text-center whitespace-nowrap sg-bold uppercase">
                    #{{ contestant.rank }}
                  </td>
                  <td class="py-1 whitespace-nowrap sg-bold uppercase">
                    <NuxtLink :to="`/champ/${contestant.name_id}`" class="wavy-text">
                      {{ contestant.name }}
                    </NuxtLink>
                  </td>

                  <td class="py-1 whitespace-nowrap text-center text-sm sg-bold hidden sm:block">
                    {{ contestant.games_participated }}
                  </td>
                  <td class="py-1 whitespace-nowrap text-center text-sm sg-bold">
                    {{ contestant.games_lost }}
                  </td>
                  <td class="py-1 whitespace-nowrap text-center text-sm sg-bold">
                    {{ contestant.score }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div
      class="p-2 mt-2 sm:mt-0 bg-green-500 items-center flex text-white sm:text-stroke-green-900 sm:text-stroke-1 uppercase justify-center tracking-wider dark:bg-gray-600"
    >
      <div class="flex items-center text-xs sm:text-xl space-x-7 sg-bold">
        <span>solo performances only.</span>
        <span>no specials.</span>

        <NuxtLink
          v-tooltip="{
            content: 'Come @ me!',
            theme: 'customTip' }
          "
          href="https://twitter.com/CrappyQuizStats"
          rel="noopener"
          target="_blank"
          class="text-white sm:text-stroke-green-900 sm:text-stroke-1"
        >
          corrections welcomed.
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style scoped>
.left-star {
  animation: 2000ms ease-in-out 250ms infinite alternate none running leftstar;
}

.middle-star {
  animation: 2000ms ease-in-out 0s infinite alternate none running middlestar;
}

.right-star {
  animation: 2000ms ease-in-out 500ms infinite alternate none running rightstar;
}

@keyframes leftstar {
  0% {
    transform: translateY(-10%);
  }

  100% {
    transform: translateY(10%);
  }
}

@keyframes middlestar {
  0% {
    transform: translateY(-25%);
  }

  100% {
    transform: translateY(-10%);
  }
}

@keyframes rightstar {
  0% {
    transform: translateY(-10%);
  }

  100% {
    transform: translateY(10%);
  }
}

.wavy-text:hover {
  text-decoration-line: underline;
  text-decoration-style: wavy;
  text-decoration-color: white;
  text-decoration-thickness: 0.08rem;
  text-decoration-skip: auto;
}

.top-1-link:hover,
.top-1-link:focus {
  text-decoration-line: underline;
  text-decoration-style: double;
  text-decoration-color: rgba(34, 197, 94, 0.5);
  text-decoration-thickness: 7px;
  text-decoration-skip: auto;
  view-transition-name: champ-name;
}

.top-3-link:hover,
.top-3-link:focus {
  text-decoration-line: underline;
  text-decoration-style: double;
  text-decoration-color: rgba(34, 197, 94, 0.5);
  text-decoration-thickness: 4px;
  text-decoration-skip: auto;
  view-transition-name: champ-name;
}

@media only screen and (max-width: 2768px) {
  .gold-cup {
    height: 276px;
  }
}

@media only screen and (max-width: 414px) {
  .gold-cup {
    height: 217px;
  }
}
</style>

<style lang="scss">
.v-popper--theme-customTip .v-popper__inner {
  background: #22c55e;
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 5px;
  font-size: 1.2rem;
}

.v-popper--theme-customTip .v-popper__arrow-outer {
  visibility: visible;
  color: #22c55e;
}

.v-popper--theme-customTip .v-popper__arrow-inner {
  visibility: hidden;
}
</style>
